import { DEFAULT_PAGE_SIZE_OPTIONS } from '@ui/constants';
import { Table } from 'antd';
import type { TableProps, ColumnsType } from 'antd/es/table';
import type { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig } from 'antd/es/table/interface';
import React, { useEffect, useRef } from 'react';

import './styles.scss';

export interface DataTableProps<T extends Record<string, any>> extends TableProps<T> {
    dataSource?: T[];
    columns?: ColumnsType<T>;
    loading?: boolean;
    rowKey?: string | ((record: T, index?: number) => string | number);
    scroll?: { x?: number | string | true; y?: number | string };
    pagination?: TablePaginationConfig | false;
    onChange?: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[],
        extra: TableCurrentDataSource<T>
    ) => void;
    itemName?: string;
    onTableChange?: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[]
    ) => void;
    /**
     * Callback when filters change
     */
    onFiltersChange?: (filters: Record<string, FilterValue | null>) => void;
    /**
     * Optional prefix for test IDs. If not provided, uses `id` prop or no test IDs.
     * Test IDs will be generated as: `${testIdPrefix}-${element-type}`
     * Example: testIdPrefix="metrics" -> "metrics-pagination-page-1", "metrics-pagination-total", etc.
     */
    testIdPrefix?: string;
}

function DataTable<T extends Record<string, any>>({
    pagination,
    itemName = 'items',
    onTableChange,
    onChange,
    onFiltersChange,
    testIdPrefix,
    ...props
}: DataTableProps<T>) {
    const tableRef = useRef<HTMLDivElement>(null);
    const tableId = props.id;
    // Use testIdPrefix if provided, otherwise fall back to id, or undefined for no test IDs
    const prefix = testIdPrefix ?? tableId;

    // Helper to generate test IDs consistently
    const getTestId = (element: string) => (prefix ? `${prefix}-${element}` : undefined);

    // Add test ID to page size selector via DOM manipulation (Ant Design limitation)
    useEffect(() => {
        if (!prefix || pagination === false) return;

        const addTestIdToPageSizeSelector = () => {
            if (!tableRef.current) return;

            const paginationContainer = tableRef.current.querySelector(
                `.${tableId}-pagination, .data-table-pagination, .ant-table-pagination`
            );
            if (paginationContainer) {
                const selectElement = paginationContainer.querySelector('.ant-select-selector');
                if (selectElement) {
                    selectElement.setAttribute('data-testid', getTestId('pagination-page-size-selector') || '');
                    return true;
                }
            }
            return false;
        };

        // Try immediately
        if (addTestIdToPageSizeSelector()) return;

        // If not found, try after a short delay (for async rendering)
        const timeoutId = setTimeout(() => {
            addTestIdToPageSizeSelector();
        }, 100);

        // Also observe for DOM changes
        const observer = new MutationObserver(() => {
            if (addTestIdToPageSizeSelector()) {
                observer.disconnect();
            }
        });

        if (tableRef.current) {
            observer.observe(tableRef.current, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, [prefix, tableId, pagination, props.dataSource, props.loading]);

    const formatPaginationText = (total: number, range: [number, number]) => {
        const capitalizedItemName = itemName?.charAt(0).toUpperCase() + itemName?.slice(1);
        return (
            <span
                className="pl-2 text-[12px]"
                data-testid={getTestId('pagination-total')}
            >
                Showing {range[0]}-{range[1]} of {total} {capitalizedItemName}
            </span>
        );
    };

    const handleTableChange = (
        paginationParam: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<T> | SorterResult<T>[],
        extra: TableCurrentDataSource<T>
    ) => {
        // Call onFiltersChange callback if provided
        if (onFiltersChange) {
            onFiltersChange(filters);
        }

        // Call both handlers for backward compatibility
        onTableChange?.(paginationParam, filters, sorter);
        onChange?.(paginationParam, filters, sorter, extra);
    };

    // Update pagination with default settings
    let finalPagination: DataTableProps<T>['pagination'] = false;
    if (pagination !== false) {
        finalPagination = {
            position: ['bottomCenter'],
            className: tableId ? `data-table-pagination ${tableId}-pagination` : 'data-table-pagination',
            showTotal: formatPaginationText,
            showSizeChanger: true,
            pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
            itemRender: (page: number, type: string, originalElement: React.ReactElement) => {
                if (type === 'page') {
                    return React.cloneElement(originalElement, {
                        'data-testid': getTestId(`pagination-page-${page}`),
                    } as any);
                }
                if (type === 'prev') {
                    return React.cloneElement(originalElement, {
                        'data-testid': getTestId('pagination-prev'),
                    } as any);
                }
                if (type === 'next') {
                    return React.cloneElement(originalElement, {
                        'data-testid': getTestId('pagination-next'),
                    } as any);
                }
                return originalElement;
            },
            ...(pagination && typeof pagination === 'object' ? pagination : {}),
        } as any;
        // Add test ID to pagination container via style injection
        if (prefix && typeof finalPagination === 'object') {
            (finalPagination as any).dataTestId = getTestId('pagination');
        }
    }

    const isFill = typeof props.className === 'string' && props.className.includes('data-table-fill');

    return (
        <div
            ref={tableRef}
            data-testid={getTestId('table-container')}
            className={isFill ? 'flex min-h-0 flex-1 flex-col' : undefined}
        >
            <Table<T>
                pagination={finalPagination}
                onChange={handleTableChange}
                style={
                    {
                        '--pagination-item-label': `"${itemName.charAt(0).toUpperCase() + itemName.slice(1)} Per Page"`,
                    } as React.CSSProperties
                }
                {...props}
                data-testid={getTestId('table')}
            />
        </div>
    );
}

export default DataTable;
